import React, { useState, useCallback, useRef } from 'react';
import _debounce from 'lodash.debounce';
import memoizeOne from 'memoize-one';
import cx from 'classnames';
import { credentials } from '@shootsta/client-auth';
import { Tabs, Input, FormModal, Text } from '../../../../../ui-kit';
import MembersTab from './MembersTab';
import AddUsersTab from './AddUsersTab';
import AddTeamsTab from './AddTeamsTab';

import '../styles/_collaboration-modal-v5.scss';
import { resolveProfileImages } from '../../../utils';
import ConfirmationModal from '../../../../ConfirmationModal';

const TEAM_FIELDS_TO_FILTER = ['name'];
const USER_FIELDS_TO_FILTER = ['firstName', 'lastName', 'email'];

















function itemMatchesFilter(item, filter) {
  const itemKeys = Object.keys(item);
  const fields = itemKeys.includes('teamId') ?
  TEAM_FIELDS_TO_FILTER :
  USER_FIELDS_TO_FILTER;

  return itemKeys.
  filter((key) => fields.includes(key)).
  some((itemKey) => {
    const itemField = item[itemKey];
    return (
      itemField &&
      typeof itemField === 'string' &&
      itemField.toLowerCase().includes(filter.toLowerCase()));

  });
}

const spreadAndCombineAuthorities = (members, authorities) => {
  const updatedMembers = members.map((member) => {
    const matchingAuthority = authorities.find(
      (authority) => authority.userId === member.userId
    );

    if (matchingAuthority) {
      return {
        ...member,
        ...matchingAuthority
      };
    }

    return member;
  });

  const unmatchedAuthorities = authorities.filter(
    (authority) => !members.some((member) => member.userId === authority.userId)
  );
  return [...updatedMembers, ...unmatchedAuthorities];
};

const CollaborationModalV5 = ({
  authorities: authorityMembers = [],
  onUpdateAndSaveMembers,
  onClose,
  onRemoveMember,
  onUpdateMember,
  onAddMember,
  visible,
  getUsersByName,
  getTeamsByName,
  organisationId,
  roles,
  members = [],
  apiClient
}) => {
  const [teams, setTeams] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [authorities, setAuthorities] = useState([]);
  const searchInputRef = useRef(null);
  const [showOwnerConfirm, setShowOwnerConfirm] = useState(false);
  const [showOwnerConfirmStep2, setShowOwnerConfirmStep2] = useState(false);
  const [pendingOwnerChange, setPendingOwnerChange] = useState(null);
  const [dropdownResetKey, setDropdownResetKey] = useState(0);

  const membersIds = members ?
  members.map(({ userId, teamId }) => userId || teamId).filter(Boolean) :
  [];

  const resolveAuthoritiesImage = async () => {
    const resolvedAuthorities = await resolveProfileImages(
      authorityMembers,
      organisationId,
      apiClient
    );
    setAuthorities(resolvedAuthorities);
  };

  React.useEffect(() => {
    if (!authorityMembers) return;
    resolveAuthoritiesImage();
  }, [authorityMembers]);

  const debouncedSearchChange = useCallback(
    _debounce(async (value) => {
      if (!value) {
        setUsers([]);
        setTeams([]);
        return;
      }

      setLoading(true);
      const { data, error } = selectedTabIndex === 1 ?
      await getUsersByName({ name: value, organisationId }) :
      await getTeamsByName({ name: value, organisationId });

      if (!data || error) {
        setLoading(false);
        return;
      }

      setUsers(data.getUsersByName);
      setTeams(data.getTeamsByName);
      setLoading(false);
    }, 250),
    [selectedTabIndex, getUsersByName, getTeamsByName, organisationId]
  );

  const handleInputChange = (e) => {
    setFilter(e.target.value);

    if (selectedTabIndex === 0) return;

    debouncedSearchChange(e.target.value);
  };

  const filterByName = useCallback(
    memoizeOne(
      (userfilter, list) => list.filter((item) => itemMatchesFilter(item, userfilter)),
      ([newFilter, newList], [oldFilter, oldList]) => newFilter === oldFilter && newList === oldList
    ),
    []
  );

  const updateSelectedTabIndex = (index) => {
    setSelectedTabIndex(index.selectedTabIndex);
    setFilter('');
    setUsers([]);
    setTeams([]);
    setLoading(false);
    if (searchInputRef.current) {
      searchInputRef.current.clearValue();
    }
  };

  const getSearchPlaceholder = () => selectedTabIndex === 0 ?
  'Search current members' :
  `Search ${selectedTabIndex === 1 ? 'users' : 'teams'}`;

  const { user: { userId: currentUserId = '' } = {} } = credentials.get() || {};
  const isCurrentUserOwner = pendingOwnerChange?.currentOwner?.userId === currentUserId;

  const canSave = () => {
    const ownersCount = members.reduce(
      (acc, member) => member.role && member.role.name === 'OWNER' ? acc + 1 : acc,
      0
    );
    return ownersCount === 1;
  };

  const handleUpdateMember = (draftMemberId, updatedItem) => {
    if (updatedItem.role?.name === 'OWNER') {
      const currentOwner = members.find((m) => m.role?.name === 'OWNER');
      if (currentOwner) {
        setPendingOwnerChange({ draftMemberId, updatedItem, currentOwner });
        setShowOwnerConfirm(true);
        return;
      }
    }
    onUpdateMember(draftMemberId, updatedItem);
  };

  const executeOwnerTransfer = () => {
    if (!pendingOwnerChange) return;
    const { draftMemberId, updatedItem, currentOwner } = pendingOwnerChange;
    const collaboratorRole = roles.find((r) => r.name === 'COLLABORATOR');
    onUpdateMember(draftMemberId, updatedItem);
    onUpdateMember(currentOwner.draftMemberId, { ...currentOwner, role: collaboratorRole });
  };

  const confirmOwnerTransfer = () => {
    if (isCurrentUserOwner) {
      setShowOwnerConfirm(false);
      setShowOwnerConfirmStep2(true);
    } else {
      executeOwnerTransfer();
      setShowOwnerConfirm(false);
      setPendingOwnerChange(null);
    }
  };

  const confirmOwnerTransferStep2 = () => {
    executeOwnerTransfer();
    setShowOwnerConfirmStep2(false);
    setPendingOwnerChange(null);
  };

  const resetOwnerTransferState = (setShowFlag) => {
    setShowFlag(false);
    setPendingOwnerChange(null);
    setDropdownResetKey((prev) => prev + 1);
  };

  const cancelOwnerTransfer = () => {
    resetOwnerTransferState(setShowOwnerConfirm);
  };

  const cancelOwnerTransferStep2 = () => {
    resetOwnerTransferState(setShowOwnerConfirmStep2);
  };

  const isSaveEnabled = canSave();

  const filteredMembers = filter ? filterByName(filter, members) : members;

  const combinedMembers = spreadAndCombineAuthorities(
    filteredMembers,
    authorities
  );

  const renderActiveTabContent = () => {
    switch (selectedTabIndex) {
      case 0:
        return (
          <MembersTab
            key={dropdownResetKey}
            members={combinedMembers}
            roles={roles}
            onRemoveMember={onRemoveMember}
            onUpdateMember={handleUpdateMember} />);


      case 1:
        return (
          <AddUsersTab
            onAddMember={onAddMember}
            users={users}
            membersIds={membersIds}
            loading={loading} />);


      case 2:
        return (
          <AddTeamsTab
            onAddMember={onAddMember}
            teams={teams}
            membersIds={membersIds}
            loading={loading} />);


      default:
        return null;
    }
  };

  return (
    <div
      className={cx('collaboration-modal-v5 ', {
        'collaboration-modal-v5--loading': loading
      })}>

      <FormModal
        visible={visible}
        onClose={onClose}
        minWidth="800px"
        saveText="Save Changes"
        saveDisabled={!isSaveEnabled}
        onSave={onUpdateAndSaveMembers}
        title="Collaborators"
        subtitle="View and manage collaborators on this project">

        <div className="collaboration-modal-v5__body">
          <Tabs updateSelectedTabIndex={updateSelectedTabIndex}>
            <div title={`Members (${combinedMembers.length})`} />
            <div title="Add Users" />
            <div title="Add Teams" />
          </Tabs>
          <div className="collaboration-modal-v5__search-container">
            <Input
              ref={searchInputRef}
              className="collaboration-modal-v5__search"
              prefix="search"
              placeholder={getSearchPlaceholder()}
              value={filter}
              onChange={handleInputChange} />

          </div>
          <div className="collaboration-modal-v5__content">
            {renderActiveTabContent()}
          </div>

          {!isSaveEnabled &&
          <Text warning>There can only be one user with the Owner role.</Text>}

        </div>
      </FormModal>
      <ConfirmationModal
        visible={showOwnerConfirm}
        onClose={cancelOwnerTransfer}
        onSave={confirmOwnerTransfer}
        title="Transfer Ownership"
        saveBtnText="Transfer Ownership"
        hideSaveBtn={!isCurrentUserOwner}>

        <Text className="pb-4">There can only be one user with the Owner role.</Text>
      </ConfirmationModal>
      <ConfirmationModal
        visible={showOwnerConfirmStep2}
        onClose={cancelOwnerTransferStep2}
        onSave={confirmOwnerTransferStep2}
        title="Transfer Ownership"
        saveBtnText="Transfer Ownership">

        <Text className="pb-4">
          Are you sure? This means you will no longer be the owner of this project.
        </Text>
      </ConfirmationModal>
    </div>);

};

export default CollaborationModalV5;